import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Search, Users, ArrowLeft, Filter, X } from 'lucide-react';
import { useIsMobile } from '../lib/hooks/use-mobile';
import { useCaregivers } from '../lib/hooks/use-caregivers-api';
import { useCaregiverCalendar } from '../lib/hooks/use-caregiver-calendar-api';
import { useCalendarSearchData } from '../lib/hooks/use-calendar-search-data';
import { useCalendarStore } from '../lib/stores/calendar-store';
import { BookingDrawer } from '../components/calendar/booking-drawer';
import { CalendarSearchDialog } from '../components/calendar/calendar-search-dialog';
import type { CalendarEvent } from '../lib/utils/calendar-helpers';
import type { NurseOperationalStatus, Caregiver } from '../lib/types/data.types';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isSameWeek,
  addMonths,
  subMonths,
  startOfDay,
  isBefore,
} from 'date-fns';

// Note: Avatars are now provided by the API or generated as placeholders
// No need for static avatar imports

// Time slots (12 AM to 11 PM) - for Day view
const timeSlots = [
  '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
  '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM'
];

// Availability filter options - these filter by NURSE OPERATIONAL STATUS, not event status
const availabilityFilterOptions: { id: NurseOperationalStatus; label: string }[] = [
  { id: 'available', label: 'Available' },
  { id: 'on_assignment', label: 'On Assignment' },
  { id: 'on_leave', label: 'On Leave' },
];

// Constants for scaling
const MIN_COLUMN_WIDTH = 140;
const TIME_COLUMN_WIDTH = 85;
const DAY_COLUMN_WIDTH = 110;
const WEEK_COLUMN_WIDTH = 160;

// Mobile-specific constants
const MOBILE_WEEK_COLUMN_WIDTH = 120;
const MOBILE_MONTH_COLUMN_WIDTH = 80;
const MOBILE_TIME_COLUMN_WIDTH = 60;

// Checkbox component matching Figma exactly
function Checkbox({ checked, onClick }: { checked: boolean; onClick?: () => void }) {
  if (checked) {
    return (
      <div
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        style={{
          width: '16px',
          height: '16px',
          backgroundColor: '#e5284c',
          borderRadius: '4px',
          boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path d="M2.5 5.5L4.5 7.5L8.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  }
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        width: '16px',
        height: '16px',
        backgroundColor: '#f3f3f5',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '4px',
        boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)',
        cursor: 'pointer',
      }}
    />
  );
}

// Avatar handling is now done in the API transformation layer
// Caregivers come with avatars already set (from API or as placeholders)

// Color palette for upcoming bookings (ping-pong pattern)
const UPCOMING_COLORS = ['#FE2C54', '#E5284C', '#CB2343', '#B21F3B'];
const PAST_COLOR = '#9ca3af';
const LEAVE_COLOR = '#e4e4e7'; // Light gray for leave events

// Get booking card color based on event type and date
function getBookingColor(event: CalendarEvent, index: number): string {
  // Leave events always get the distinct leave color
  if (event.type === 'leave') {
    return LEAVE_COLOR;
  }

  const now = new Date();

  // Event is past if date is before today (ignoring time)
  // This handles test backend cases where status='SCHEDULED' but date is in past
  const isPast = isBefore(startOfDay(event.startDate), startOfDay(now));

  if (isPast) {
    return PAST_COLOR;  // Gray for past events
  }

  // Colored for today and future events
  const cycleLength = (UPCOMING_COLORS.length - 1) * 2;
  const position = index % cycleLength;

  if (position < UPCOMING_COLORS.length) {
    return UPCOMING_COLORS[position];
  } else {
    return UPCOMING_COLORS[cycleLength - position];
  }
}

// Week View Event Card Component
function WeekEventCard({
  event,
  onClick,
  color,
  isMobile
}: {
  event: CalendarEvent;
  onClick: () => void;
  color: string;
  isMobile?: boolean;
}) {
  const getEventTime = () => {
    const times = [
      '9:00 AM - 12:00 PM', '6:00 PM - 9:00 PM', '2:00 PM - 4:00 PM',
      '9:00 AM - 5:00 PM', '10:00 AM - 6:00 AM', '10:00 AM - 11:30 AM',
      '3:00 PM - 6:00 PM', '7:00 PM - 9:00 PM', '1:00 PM - 3:00 PM',
    ];
    const hash = event.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return times[hash % times.length];
  };

  const isLeave = event.type === 'leave';

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: color,
        borderRadius: isMobile ? '6px' : '8px',
        padding: isMobile ? '6px 8px' : '8px 10px',
        cursor: 'pointer',
        transition: 'opacity 0.15s',
        border: isLeave ? '2px dashed #9ca3af' : 'none',
      }}
      onMouseEnter={!isMobile ? (e) => { e.currentTarget.style.opacity = '0.95'; } : undefined}
      onMouseLeave={!isMobile ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
      onTouchStart={isMobile ? (e) => { e.currentTarget.style.opacity = '0.7'; } : undefined}
      onTouchEnd={isMobile ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
    >
      <p style={{
        fontSize: isMobile ? '10px' : '11px',
        fontWeight: 500,
        color: isLeave ? '#71717a' : 'white',
        lineHeight: '14px',
        margin: 0,
      }}>
        {getEventTime()}
      </p>
      <p style={{
        fontSize: isMobile ? '11px' : '12px',
        fontWeight: 600,
        color: isLeave ? '#71717a' : 'white',
        lineHeight: '16px',
        margin: '4px 0 0 0',
      }}>
        {event.title}
      </p>
    </div>
  );
}

// On Leave Card Component
function OnLeaveCard() {
  return (
    <div style={{
      backgroundColor: '#f4f4f5',
      borderRadius: '8px',
      minHeight: '56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
    }}>
      <span style={{
        fontSize: '12px',
        fontWeight: 500,
        color: '#71717a',
      }}>On Leave</span>
    </div>
  );
}

// Available Caregivers Modal Component
function AvailableCaregiversModal({
  isOpen,
  onClose,
  date,
  caregivers,
}: {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  caregivers: Caregiver[];
}) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          zIndex: 9999,
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #f4f4f5',
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            color: '#18181b',
          }}>
            Available Caregivers
          </h3>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '14px',
            color: '#71717a',
          }}>
            {format(date, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px 24px',
          overflowY: 'auto',
          flex: 1,
        }}>
          {caregivers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#71717a',
              fontSize: '14px',
            }}>
              No available caregivers on this day
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              {caregivers.map((caregiver) => (
                <div
                  key={caregiver.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    border: '1px solid #f4f4f5',
                  }}
                >
                  <img
                    src={caregiver.avatar}
                    alt={caregiver.fullName}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#18181b',
                    }}>
                      {caregiver.fullName}
                    </p>
                    <p style={{
                      margin: '2px 0 0 0',
                      fontSize: '12px',
                      color: '#71717a',
                    }}>
                      {caregiver.city}
                    </p>
                  </div>
                  <div style={{
                    padding: '4px 8px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#16a34a',
                  }}>
                    Available
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f4f4f5',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#18181b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// Month View Event Card Component
function MonthEventCard({
  event,
  caregiverName,
  caregiverAvatar,
  onClick,
  color,
  isMobile
}: {
  event: CalendarEvent;
  caregiverName: string;
  caregiverAvatar: string;
  onClick: () => void;
  color: string;
  isMobile?: boolean;
}) {
  const getEventTime = () => {
    const times = [
      '9:00 AM - 5:00 PM', '10:00 AM - 6:00 PM', '2:00 PM - 10:00 PM',
      '7:00 AM - 3:00 PM', '1:00 PM - 9:00 PM', '3:00 PM - 11:00 PM',
    ];
    const hash = event.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return times[hash % times.length];
  };

  const isLeave = event.type === 'leave';

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: color,
        borderRadius: isMobile ? '5px' : '6px',
        height: isMobile ? '50px' : '60px',
        padding: isMobile ? '5px' : '6px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'opacity 0.15s',
        display: 'flex',
        flexDirection: 'column',
        border: isLeave ? '2px dashed #9ca3af' : 'none',
      }}
      onMouseEnter={!isMobile ? (e) => { e.currentTarget.style.opacity = '0.95'; } : undefined}
      onMouseLeave={!isMobile ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
      onTouchStart={isMobile ? (e) => { e.currentTarget.style.opacity = '0.7'; } : undefined}
      onTouchEnd={isMobile ? (e) => { e.currentTarget.style.opacity = '1'; } : undefined}
    >
      <span style={{
        fontSize: isMobile ? '9px' : '10px',
        fontWeight: 500,
        color: isLeave ? '#71717a' : 'white',
        opacity: 0.9,
        lineHeight: '12px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
      }}>
        {getEventTime()}
      </span>
      <span style={{
        fontSize: isMobile ? '10px' : '11px',
        fontWeight: 600,
        color: isLeave ? '#71717a' : 'white',
        lineHeight: '14px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        marginTop: '2px',
      }}>
        {event.title}
      </span>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: 'auto',
      }}>
        <div style={{
          width: '16px',
          height: '16px',
          minWidth: '16px',
          minHeight: '16px',
          borderRadius: '50%',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.3)',
          flexShrink: 0,
        }}>
          <img
            src={caregiverAvatar}
            alt={caregiverName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>
        <span style={{
          fontSize: '10px',
          fontWeight: 600,
          color: isLeave ? '#71717a' : 'white',
          lineHeight: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {caregiverName}
        </span>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const isMobile = useIsMobile();
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [mobileDrawerAnimating, setMobileDrawerAnimating] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);

  // Get state from store
  const {
    view,
    currentDate,
    filters,
    setView,
    setCurrentDate,
    setSelectedCaregivers,
    setStatusFilter,
  } = useCalendarStore();

  // Map view to viewMode (store uses 'view', calendar helpers use 'viewMode')
  const viewMode = view === 'timeline' ? 'week' : view;
  const selectedCaregiverIds = filters.selectedCaregivers;

  // Fetch caregivers from API
  const { caregivers: allCaregivers, loading: caregiversLoading, isWaitingForVendor } = useCaregivers({
    status: 'ACTIVE',
  });

  // Determine which caregivers to fetch calendar for (use memo to prevent infinite loop)
  const caregiversToFetch = useMemo(() => {
    // Only fetch if there are selected caregivers, otherwise return empty array
    return selectedCaregiverIds.length > 0 ? selectedCaregiverIds : [];
  }, [selectedCaregiverIds]);

  // Fetch calendar events for selected caregivers
  const { events, loading: eventsLoading } = useCaregiverCalendar(
    caregiversToFetch,
    viewMode as 'day' | 'week' | 'month',
    currentDate
  );

  // Fetch broader date range for search (all caregivers, 6 months of data)
  const allCaregiverIds = useMemo(() => allCaregivers.map(cg => cg.id), [allCaregivers]);
  const { events: searchEvents, loading: searchEventsLoading } = useCalendarSearchData(allCaregiverIds);

  // Combined loading state
  const isLoading = caregiversLoading || eventsLoading;

  // Initial loading state (show skeleton until first successful load)
  const isInitialLoading = !hasLoadedOnce && caregiversLoading;

  // Helper actions
  const setViewMode = (mode: 'day' | 'week' | 'month') => setView(mode);
  const setSelectedCaregiverIds = setSelectedCaregivers;

  const [nurseSearchQuery, setNurseSearchQuery] = useState('');
  const [availabilitySearchQuery, setAvailabilitySearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<CalendarEvent | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hoveredDateKey, setHoveredDateKey] = useState<string | null>(null);
  const [hoveredCaregiverId, setHoveredCaregiverId] = useState<string | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [selectedDayAvailability, setSelectedDayAvailability] = useState<{
    date: Date;
    caregivers: Caregiver[];
  } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const measureWidth = () => {
      if (gridContainerRef.current) {
        setContainerWidth(gridContainerRef.current.offsetWidth);
      }
    };
    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => window.removeEventListener('resize', measureWidth);
  }, []);

  // Track when caregivers have loaded at least once
  useEffect(() => {
    if (!caregiversLoading && allCaregivers.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [caregiversLoading, allCaregivers.length]);

  // Select first 10 caregivers by default when they first load (only once on initial load)
  useEffect(() => {
    if (!caregiversLoading && allCaregivers.length > 0 && selectedCaregiverIds.length === 0 && !hasInitializedSelection) {
      const first10CaregiverIds = allCaregivers.slice(0, 10).map(cg => cg.id);
      setSelectedCaregivers(first10CaregiverIds);
      setHasInitializedSelection(true);
    }
  }, [caregiversLoading, allCaregivers, selectedCaregiverIds.length, hasInitializedSelection, setSelectedCaregivers]);

  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate]);

  const monthGridDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    return eachDayOfInterval({ start: gridStart, end: gridEnd }).map(day => ({
      date: day,
      dayNumber: day.getDate(),
      isCurrentMonth: isSameMonth(day, currentDate),
      isToday: isSameDay(day, new Date()),
    }));
  }, [currentDate]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return days.map((day, index) => ({
      date: day,
      day: day.getDate(),
      isCurrentMonth: isSameMonth(day, currentDate),
      isSelected: (viewMode === 'day' || viewMode === 'month') ? isSameDay(day, currentDate) : false,
      isInSelectedWeek: viewMode === 'week' ? isSameWeek(day, currentDate, { weekStartsOn: 0 }) : false,
      row: Math.floor(index / 7),
      col: index % 7,
    }));
  }, [currentDate, viewMode]);

  const filteredCaregivers = useMemo(() => {
    if (!nurseSearchQuery) return allCaregivers;
    const query = nurseSearchQuery.toLowerCase();
    return allCaregivers.filter(cg =>
      cg.fullName.toLowerCase().includes(query) ||
      cg.city.toLowerCase().includes(query)
    );
  }, [allCaregivers, nurseSearchQuery]);

  const filteredAvailabilityOptions = useMemo(() => {
    if (!availabilitySearchQuery) return availabilityFilterOptions;
    const query = availabilitySearchQuery.toLowerCase();
    return availabilityFilterOptions.filter(opt =>
      opt.label.toLowerCase().includes(query)
    );
  }, [availabilitySearchQuery]);

  const areAllNursesSelected = useMemo(() => {
    return filteredCaregivers.length > 0 &&
      filteredCaregivers.every(cg => selectedCaregiverIds.includes(cg.id));
  }, [filteredCaregivers, selectedCaregiverIds]);

  const displayedCaregivers = useMemo(() => {
    if (selectedCaregiverIds.length === 0) {
      return [];
    }

    let caregivers = allCaregivers.filter(cg => selectedCaregiverIds.includes(cg.id));

    if (filters.statusFilter.length > 0) {
      caregivers = caregivers.filter(cg => {
        // Get all events for this caregiver in the current view
        const caregiverEvents = events.filter(e => e.caregiverId === cg.id);

        // Check if nurse matches ANY of the selected filters (OR logic)
        return filters.statusFilter.some(selectedStatus => {
          if (selectedStatus === 'on_leave') {
            // Has any leave events in the current view
            return caregiverEvents.some(e => e.type === 'leave') || cg.operationalStatus === 'on_leave';
          }
          if (selectedStatus === 'on_assignment') {
            // Has any assignment events in the current view
            return caregiverEvents.some(e => e.type === 'assignment');
          }
          if (selectedStatus === 'available') {
            // Has NO events at all in the current view
            return caregiverEvents.length === 0 && cg.operationalStatus !== 'on_leave';
          }
          return false;
        });
      });
    }

    // Sort caregivers by number of assignments in current view (descending)
    // Nurses with most assignments appear first
    caregivers.sort((a, b) => {
      const aAssignments = events.filter(e => e.caregiverId === a.id && e.type === 'assignment').length;
      const bAssignments = events.filter(e => e.caregiverId === b.id && e.type === 'assignment').length;
      return bAssignments - aAssignments; // Descending order
    });

    return caregivers;
  }, [allCaregivers, selectedCaregiverIds, filters.statusFilter, events]);

  const columnWidth = useMemo(() => {
    if (displayedCaregivers.length === 0) return MIN_COLUMN_WIDTH;

    // Use mobile widths on mobile devices
    if (isMobile) {
      return viewMode === 'week' ? MOBILE_WEEK_COLUMN_WIDTH : MIN_COLUMN_WIDTH;
    }

    // Desktop calculations
    if (viewMode === 'week') {
      const availableWidth = containerWidth - DAY_COLUMN_WIDTH;
      const naturalWidth = availableWidth / displayedCaregivers.length;
      return Math.max(naturalWidth, WEEK_COLUMN_WIDTH);
    }
    const availableWidth = containerWidth - TIME_COLUMN_WIDTH;
    const naturalWidth = availableWidth / displayedCaregivers.length;
    return Math.max(naturalWidth, MIN_COLUMN_WIDTH);
  }, [displayedCaregivers.length, containerWidth, viewMode, isMobile]);

  const needsHorizontalScroll = viewMode === 'week'
    ? columnWidth === WEEK_COLUMN_WIDTH && displayedCaregivers.length > 0
    : columnWidth === MIN_COLUMN_WIDTH && displayedCaregivers.length > 0;

  const filteredEvents = useMemo(() => {
    const displayedIds = displayedCaregivers.map(cg => cg.id);

    return events.filter(event => {
      if (!displayedIds.includes(event.caregiverId)) return false;

      if (viewMode === 'day') {
        return isSameDay(event.startDate, currentDate);
      }

      if (viewMode === 'week') {
        return weekDays.some(day => isSameDay(event.startDate, day));
      }

      if (viewMode === 'month') {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
        const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
        return event.startDate >= gridStart && event.startDate <= gridEnd;
      }

      return true;
    });
  }, [events, displayedCaregivers, viewMode, currentDate, weekDays]);

  const getEventsForCaregiverDay = (caregiverId: string, day: Date): CalendarEvent[] => {
    return filteredEvents.filter(event =>
      event.caregiverId === caregiverId && isSameDay(event.startDate, day)
    );
  };

  const getEventsForDay = (day: Date): CalendarEvent[] => {
    return filteredEvents.filter(event => isSameDay(event.startDate, day));
  };

  const getCaregiverWithAvatar = (caregiverId: string): { name: string; avatar: string } => {
    const caregiver = allCaregivers.find(cg => cg.id === caregiverId);
    return {
      name: caregiver?.fullName || 'Unknown',
      avatar: caregiver?.avatar || '', // Use avatar from API or placeholder
    };
  };

  const isCaregiverOnLeave = (caregiver: Caregiver, day: Date): boolean => {
    // Check if caregiver has a leave event for this day from API data
    const dayString = format(day, 'yyyy-MM-dd');
    const hasLeaveEvent = events.some(
      event =>
        event.caregiverId === caregiver.id &&
        event.type === 'leave' &&
        format(event.startDate, 'yyyy-MM-dd') === dayString
    );
    return hasLeaveEvent;
  };

  const handleNurseToggle = (caregiverId: string) => {
    if (selectedCaregiverIds.includes(caregiverId)) {
      setSelectedCaregiverIds(selectedCaregiverIds.filter(id => id !== caregiverId));
    } else {
      setSelectedCaregiverIds([...selectedCaregiverIds, caregiverId]);
    }
  };

  const handleSelectAllNurses = () => {
    if (areAllNursesSelected) {
      setSelectedCaregiverIds([]);
    } else {
      setSelectedCaregiverIds(filteredCaregivers.map(cg => cg.id));
    }
  };

  // Mobile drawer animation handlers
  const openMobileDrawer = () => {
    setMobileDrawerOpen(true);
    setMobileDrawerAnimating(true);
    // Small delay to trigger animation
    setTimeout(() => {
      setMobileDrawerVisible(true);
    }, 10);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerVisible(false);
    setMobileDrawerAnimating(true);
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setMobileDrawerOpen(false);
      setMobileDrawerAnimating(false);
    }, 300); // Match transition duration
  };

  const handleAvailabilityToggle = (status: string) => {
    if (filters.statusFilter.includes(status)) {
      setStatusFilter(filters.statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...filters.statusFilter, status]);
    }
  };

  const handleDateSelect = (date: Date) => {
    // Prevent unnecessary reloads when clicking dates within the current view period
    if (viewMode === 'month') {
      // In month view, only update if clicking a date from a different month
      if (!isSameMonth(date, currentDate)) {
        setCurrentDate(date);
      }
    } else if (viewMode === 'week') {
      // In week view, only update if clicking a date from a different week
      if (!isSameWeek(date, currentDate, { weekStartsOn: 0 })) {
        setCurrentDate(date);
      }
    } else {
      // In day view, always update to the clicked date
      setCurrentDate(date);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleViewChange = (view: 'day' | 'week' | 'month') => {
    setViewMode(view);
  };

  const handleBookingClick = (event: CalendarEvent) => {
    setSelectedBooking(event);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedBooking(null);
  };

  const calendarRows = useMemo(() => {
    const rows = new Set(calendarDays.map(d => d.row));
    return Array.from(rows);
  }, [calendarDays]);

  const selectedBookingCaregiver = useMemo(() => {
    if (!selectedBooking) return null;
    return allCaregivers.find(cg => cg.id === selectedBooking.caregiverId) || null;
  }, [selectedBooking, allCaregivers]);

  // Don't render anything until vendor is ready
  if (isWaitingForVendor) {
    return null;
  }

  // Show skeleton loader during initial load
  if (isInitialLoading) {
    const skeletonPulse = {
      animation: 'pulse 1.5s ease-in-out infinite',
    };

    return (
      <>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <div style={{ height: '100vh', backgroundColor: '#fafafa', padding: isMobile ? '0' : '10px' }}>
          <div style={{ height: isMobile ? '100vh' : 'calc(100vh - 20px)', backgroundColor: 'white', borderRadius: isMobile ? '0' : '14px', boxShadow: isMobile ? 'none' : '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Mobile Header Skeleton */}
            {isMobile ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e4e4e7', backgroundColor: 'white', flexShrink: 0 }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                <div style={{ flex: 1, marginLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ width: '80px', height: '14px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                  <div style={{ width: '150px', height: '11px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                </div>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
              </div>
            ) : (
              /* Desktop Header Skeleton */
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 26px', height: '60px', borderBottom: '1px solid #e4e4e7', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '19px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                  <div style={{ width: '1px', height: '16px', backgroundColor: '#e4e4e7' }} />
                  <div style={{ width: '80px', height: '16px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                  <div style={{ width: '200px', height: '16px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ width: '120px', height: '32px', backgroundColor: '#f4f4f5', borderRadius: '6px', ...skeletonPulse }} />
                  <div style={{ width: '150px', height: '32px', backgroundColor: '#f4f4f5', borderRadius: '6px', ...skeletonPulse }} />
                </div>
              </div>
            )}

            {/* Content Area Skeleton - only show on desktop */}
            {!isMobile && (
              <div style={{ display: 'flex', flex: 1, minHeight: 0, padding: '32px', gap: '24px' }}>
                {/* Left Panel Skeleton */}
                <div style={{ width: '314px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Mini Calendar Skeleton */}
                  <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px', padding: '13px' }}>
                    <div style={{ height: '32px', backgroundColor: '#f4f4f5', borderRadius: '8px', marginBottom: '17px', ...skeletonPulse }} />
                    <div style={{ height: '30px', backgroundColor: '#f4f4f5', borderRadius: '6px', marginBottom: '17px', ...skeletonPulse }} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
                      {[...Array(7)].map((_, i) => (
                        <div key={i} style={{ height: '24px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                      {[...Array(35)].map((_, i) => (
                        <div key={i} style={{ height: '32px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                      ))}
                    </div>
                  </div>

                  {/* Nurses List Skeleton */}
                  <div style={{ border: '1px solid #e4e4e7', borderRadius: '13px', padding: '14px' }}>
                    <div style={{ height: '20px', width: '100px', backgroundColor: '#f4f4f5', borderRadius: '4px', marginBottom: '12px', ...skeletonPulse }} />
                    <div style={{ height: '40px', backgroundColor: '#f4f4f5', borderRadius: '6px', marginBottom: '12px', ...skeletonPulse }} />
                    {[...Array(5)].map((_, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', marginBottom: '4px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#f4f4f5', ...skeletonPulse }} />
                        <div style={{ flex: 1, height: '14px', backgroundColor: '#f4f4f5', borderRadius: '4px', ...skeletonPulse }} />
                      </div>
                    ))}
                  </div>

                  {/* Availability Skeleton */}
                  <div style={{ border: '1px solid #e4e4e7', borderRadius: '13px', padding: '14px' }}>
                    <div style={{ height: '20px', width: '120px', backgroundColor: '#f4f4f5', borderRadius: '4px', marginBottom: '12px', ...skeletonPulse }} />
                    <div style={{ height: '40px', backgroundColor: '#f4f4f5', borderRadius: '6px', marginBottom: '12px', ...skeletonPulse }} />
                    {[...Array(3)].map((_, i) => (
                      <div key={i} style={{ height: '36px', backgroundColor: '#f4f4f5', borderRadius: '8px', marginBottom: '4px', ...skeletonPulse }} />
                    ))}
                  </div>
                </div>

                {/* Calendar Grid Skeleton */}
                <div style={{ flex: 1, minHeight: 0, border: '1px solid #e4e4e7', borderRadius: '13px', padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', height: '100%' }}>
                    {[...Array(35)].map((_, i) => (
                      <div key={i} style={{ backgroundColor: '#f4f4f5', borderRadius: '8px', minHeight: '120px', ...skeletonPulse }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div style={{ height: '100vh', backgroundColor: '#fafafa', padding: isMobile ? '0' : '10px' }}>
      {/* Main Content Container */}
      <div style={{ height: isMobile ? '100vh' : 'calc(100vh - 20px)', backgroundColor: 'white', borderRadius: isMobile ? '0' : '14px', boxShadow: isMobile ? 'none' : '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Header (only visible on mobile) */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: '1px solid #e4e4e7',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 30,
            gap: '12px',
          }}>
            <a href="/" style={{
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer',
            }}>
              <ArrowLeft size={20} color="#09090b" strokeWidth={2} />
            </a>
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '2px',
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#09090b',
                lineHeight: 1,
              }}>
                Calendar
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 500,
                color: 'rgba(9, 9, 11, 0.5)',
                letterSpacing: '-0.2px',
                lineHeight: 1,
              }}>
                {format(currentDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
            <button
              onClick={openMobileDrawer}
              style={{
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              <Filter style={{ width: '20px', height: '20px', color: '#18181b' }} />
            </button>
          </div>
        )}

        {/* Desktop Header (hidden on mobile) */}
        <div style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 26px', height: '60px', borderBottom: '1px solid #e4e4e7', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '19px' }}>
            <a href="/" style={{ padding: '4px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <ArrowLeft size={20} color="#09090b" strokeWidth={2} />
            </a>
            <div style={{ width: '1px', height: '16px', backgroundColor: '#e4e4e7' }} />
            <span style={{ fontSize: '16px', fontWeight: 500, color: '#09090b' }}>Calendar</span>
            <span style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(9, 9, 11, 0.5)', letterSpacing: '-0.3px' }}>
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => setSearchOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 12px', height: '32px', border: '1px solid #e4e4e7', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', transition: 'background-color 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            >
              <span style={{ fontSize: '12px', color: '#71717b' }}>Search...</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '4px 8px', backgroundColor: '#f4f4f5', border: '1.5px solid #e4e4e7', borderRadius: '24px' }}>
                <span style={{ fontSize: '12px', color: '#71717b', lineHeight: 1 }}>⌘</span>
                <span style={{ fontSize: '10px', color: '#71717b', lineHeight: 1 }}>K</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, padding: isMobile ? '0' : '32px', gap: '24px' }}>
          {/* Left Panel - hidden on mobile */}
          <div style={{ width: isMobile ? '0' : '314px', display: isMobile ? 'none' : 'flex', flexShrink: 0, flexDirection: 'column', gap: '10px', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Mini Calendar */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px', padding: '13px' }}>
              {/* View Tabs */}
              <div style={{ display: 'flex', alignItems: 'center', borderRadius: '8px', padding: '4px', marginBottom: '17px', backgroundColor: '#f4f4f5' }}>
                {(['day', 'week', 'month'] as const).map((view) => (
                  <button
                    key={view}
                    onClick={() => handleViewChange(view)}
                    style={{
                      flex: 1,
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 500,
                      borderRadius: '6px',
                      transition: 'all 0.15s',
                      textTransform: 'capitalize',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: viewMode === view ? '#ffffff' : 'transparent',
                      color: viewMode === view ? '#09090b' : '#71717a',
                      boxShadow: viewMode === view ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' : 'none',
                    }}
                  >
                    {view.charAt(0).toUpperCase() + view.slice(1)}
                  </button>
                ))}
              </div>

              {/* Month Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '17px', paddingTop: '4px' }}>
                <button
                  onClick={handlePreviousMonth}
                  style={{
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #f4f4f5',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <ChevronLeft style={{ width: '17px', height: '17px', color: '#a1a1aa' }} />
                </button>
                <span style={{ fontSize: '15px', fontWeight: 500, color: '#09090b' }}>
                  {format(currentDate, 'MMMM yyyy')}
                </span>
                <button
                  onClick={handleNextMonth}
                  style={{
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #f4f4f5',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <ChevronRight style={{ width: '17px', height: '17px', color: '#65656e' }} />
                </button>
              </div>

              {/* Calendar Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {/* Day headers */}
                <div style={{ display: 'flex' }}>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div key={day} style={{ width: '38.6px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '14.5px', color: '#71717a' }}>{day}</span>
                    </div>
                  ))}
                </div>
                {/* Calendar days */}
                {calendarRows.map((rowIndex) => {
                  const rowDays = calendarDays.filter(d => d.row === rowIndex);
                  const isWeekSelectedRow = viewMode === 'week' && rowDays.some(d => d.isInSelectedWeek);
                  const hasCurrentMonthDays = viewMode === 'month' && rowDays.some(d => d.isCurrentMonth);

                  // For month view, find first and last current month day indices in this row
                  const firstCurrentMonthIndex = rowDays.findIndex(d => d.isCurrentMonth);
                  const lastCurrentMonthIndex = rowDays.map(d => d.isCurrentMonth).lastIndexOf(true);

                  return (
                    <div
                      key={rowIndex}
                      style={{
                        display: 'flex',
                        position: 'relative',
                        height: '38.6px',
                      }}
                    >
                      {/* Week selection background pill */}
                      {isWeekSelectedRow && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: `${7 * 38.6}px`,
                            height: '38.6px',
                            backgroundColor: '#fce7eb',
                            borderRadius: '9999px',
                          }}
                        />
                      )}
                      {/* Month highlight background pill */}
                      {hasCurrentMonthDays && !isWeekSelectedRow && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: `${firstCurrentMonthIndex * 38.6}px`,
                            width: `${(lastCurrentMonthIndex - firstCurrentMonthIndex + 1) * 38.6}px`,
                            height: '38.6px',
                            backgroundColor: '#fce7eb',
                            borderRadius: '9999px',
                          }}
                        />
                      )}
                      {rowDays.map((date, i) => {
                        const isFirstDayOfSelectedWeek = viewMode === 'week' && date.isInSelectedWeek && date.col === 0;
                        const isLastDayOfSelectedWeek = viewMode === 'week' && date.isInSelectedWeek && date.col === 6;
                        const isSelected = viewMode === 'day' && date.isSelected;
                        const isNotCurrentMonth = !date.isCurrentMonth;
                        const isInSelectedWeek = viewMode === 'week' && date.isInSelectedWeek;
                        const isInCurrentMonth = viewMode === 'month' && date.isCurrentMonth;

                        // Month view: first day of month (day 1) and last day of month
                        const isFirstDayOfMonth = viewMode === 'month' && date.isCurrentMonth && date.day === 1;
                        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                        const isLastDayOfMonth = viewMode === 'month' && date.isCurrentMonth && date.day === lastDayOfMonth;

                        let bgColor = 'transparent';
                        let textColor = '#09090b';
                        let opacity = 1;

                        if (isSelected || isLastDayOfMonth) {
                          bgColor = '#ec003f';
                          textColor = '#ffffff';
                        } else if (isFirstDayOfMonth) {
                          bgColor = '#f9b4c4';
                          textColor = '#09090b';
                        } else if (isLastDayOfSelectedWeek) {
                          // In week view, last day gets red background like month view
                          bgColor = '#ec003f';
                          textColor = '#ffffff';
                        } else if (isFirstDayOfSelectedWeek) {
                          // In week view, first day gets light pink background like month view
                          bgColor = '#f9b4c4';
                          textColor = '#09090b';
                        } else if (isNotCurrentMonth && !isInSelectedWeek) {
                          opacity = 0.5;
                          textColor = '#71717a';
                        } else if (isNotCurrentMonth && isInSelectedWeek) {
                          textColor = '#71717a';
                        }

                        const dateKey = `${rowIndex}-${i}`;
                        const canHover = !isSelected && !isFirstDayOfSelectedWeek && !isLastDayOfSelectedWeek && !isInSelectedWeek && !isInCurrentMonth;
                        const isHovered = hoveredDateKey === dateKey && canHover;
                        const finalBgColor = isHovered ? '#f4f4f5' : bgColor;

                        return (
                          <div
                            key={i}
                            onClick={() => handleDateSelect(date.date)}
                            style={{
                              width: '38.6px',
                              height: '38.6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '17px',
                              fontWeight: 500,
                              letterSpacing: '-0.06em',
                              cursor: 'pointer',
                              transition: 'background-color 0.15s',
                              backgroundColor: finalBgColor,
                              color: textColor,
                              borderRadius: '50%',
                              opacity: opacity,
                              position: 'relative',
                              zIndex: 1,
                            }}
                            onMouseEnter={() => {
                              if (canHover) {
                                setHoveredDateKey(dateKey);
                              }
                            }}
                            onMouseLeave={() => {
                              setHoveredDateKey(null);
                            }}
                          >
                            {date.day}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nurses Section */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Users style={{ width: '14px', height: '14px', color: '#71717a' }} />
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#09090b' }}>
                    Nurses ({selectedCaregiverIds.length} / {allCaregivers.length})
                  </span>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                  onClick={handleSelectAllNurses}
                >
                  <span style={{ fontSize: '11px', color: '#71717a' }}>Select all</span>
                  <Checkbox checked={areAllNursesSelected} onClick={handleSelectAllNurses} />
                </div>
              </div>

              <div style={{ padding: '0 12px 8px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 7px', backgroundColor: '#f4f4f5', borderRadius: '6px' }}>
                  <Search style={{ width: '13px', height: '13px', color: '#71717a' }} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={nurseSearchQuery}
                    onChange={(e) => setNurseSearchQuery(e.target.value)}
                    style={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      fontSize: '14px',
                      color: '#09090b',
                      border: 'none',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <div style={{ padding: '0 12px 12px 12px', maxHeight: '300px', overflowY: 'auto' }}>
                {filteredCaregivers.map((caregiver) => {
                  const isSelected = selectedCaregiverIds.includes(caregiver.id);
                  return (
                    <div
                      key={caregiver.id}
                      onClick={() => handleNurseToggle(caregiver.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '11px 8px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <Checkbox checked={isSelected} onClick={() => handleNurseToggle(caregiver.id)} />
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden', backgroundColor: '#dbeafe' }}>
                        <img
                          src={caregiver.avatar}
                          alt={caregiver.fullName}
                          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                      </div>
                      <span style={{ fontSize: '14px', color: '#0a0a0a', letterSpacing: '-0.15px' }}>
                        {caregiver.fullName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Availability Section */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px' }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5.83312 11.6667C5.83307 11.7751 5.86323 11.8813 5.92021 11.9736C5.97719 12.0658 6.05874 12.1403 6.15571 12.1888L7.32237 12.7721C7.41133 12.8165 7.51017 12.8375 7.60951 12.833C7.70886 12.8285 7.8054 12.7987 7.88997 12.7464C7.97455 12.6941 8.04434 12.621 8.09273 12.5341C8.14113 12.4473 8.1665 12.3494 8.16646 12.25V8.16667C8.16659 7.87756 8.27406 7.59879 8.46804 7.38442L12.6815 2.72417C12.757 2.64049 12.8066 2.53673 12.8244 2.42543C12.8422 2.31413 12.8274 2.20006 12.7817 2.09701C12.7361 1.99396 12.6615 1.90635 12.5671 1.84476C12.4727 1.78318 12.3625 1.75027 12.2498 1.75H1.74979C1.63698 1.75004 1.5266 1.78279 1.43202 1.84429C1.33744 1.90578 1.26272 1.99338 1.21692 2.09648C1.17111 2.19957 1.15618 2.31374 1.17394 2.42514C1.19169 2.53655 1.24137 2.64042 1.31696 2.72417L5.53154 7.38442C5.72552 7.59879 5.83299 7.87756 5.83312 8.16667V11.6667Z" stroke="#71717a" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#09090b' }}>Availability</span>
              </div>

              <div style={{ padding: '0 12px 8px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 7px', backgroundColor: '#f4f4f5', borderRadius: '6px' }}>
                  <Search style={{ width: '13px', height: '13px', color: '#71717a' }} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={availabilitySearchQuery}
                    onChange={(e) => setAvailabilitySearchQuery(e.target.value)}
                    style={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      fontSize: '14px',
                      color: '#09090b',
                      border: 'none',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <div style={{ padding: '0 12px 12px 12px' }}>
                {filteredAvailabilityOptions.map((filter) => (
                  <div
                    key={filter.id}
                    onClick={() => handleAvailabilityToggle(filter.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Checkbox checked={filters.statusFilter.includes(filter.id)} onClick={() => handleAvailabilityToggle(filter.id)} />
                    <span style={{ fontSize: '14px', color: '#0a0a0a', letterSpacing: '-0.15px' }}>{filter.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div
            ref={gridContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
              ...(isMobile ? {
                borderLeft: '1px solid #e4e4e7',
                borderRight: '1px solid #e4e4e7',
                borderBottom: '1px solid #e4e4e7',
                borderTop: 'none',
              } : {
                border: '1px solid #e4e4e7',
              }),
              borderRadius: isMobile ? '0' : '13px',
              overflow: 'auto',
              overflowX: isMobile ? 'auto' : 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Loading State */}
            {isLoading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #f4f4f5',
                    borderTop: '4px solid #e5284c',
                    borderRadius: '50%',
                    margin: '0 auto 16px auto',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#09090b' }}>Loading calendar...</h3>
                  <p style={{ fontSize: '14px', color: '#71717a', marginTop: '4px' }}>
                    Fetching schedules from server
                  </p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              </div>
            ) : displayedCaregivers.length === 0 ? (
              /* Empty State */
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <Users style={{ width: '48px', height: '48px', color: '#d4d4d8', margin: '0 auto 16px auto' }} />
                  {selectedCaregiverIds.length === 0 ? (
                    <>
                      <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#09090b' }}>No nurses selected</h3>
                      <p style={{ fontSize: '14px', color: '#71717a', marginTop: '4px', maxWidth: '280px' }}>
                        Select nurses from the left panel to view their schedules
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#09090b' }}>No nurses match the selected filters</h3>
                      <p style={{ fontSize: '14px', color: '#71717a', marginTop: '4px', maxWidth: '280px' }}>
                        Try adjusting your availability filters or select different nurses
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : viewMode === 'month' ? (
              /* Month View */
              <div style={{
                height: '100%',
                overflow: 'auto',
              }}>
                {/* Combined Grid Container */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))',
                  minWidth: isMobile ? '700px' : '100%',
                }}>
                  {/* Day Headers Row */}
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
                    <div
                      key={day}
                      style={{
                        height: '32px',
                        padding: '0 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        borderLeft: idx === 0 ? 'none' : '1px solid #e4e4e7',
                        borderBottom: '1px solid #e4e4e7',
                        backgroundColor: 'white',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#71717a' }}>{day}</span>
                    </div>
                  ))}

                  {/* Calendar Cells */}
                  {monthGridDays.map((day, index) => {
                    const dayEvents = getEventsForDay(day.date);
                    const isLastRow = index >= monthGridDays.length - 7;

                    return (
                      <div
                        key={index}
                        style={{
                          minHeight: '169px',
                          borderLeft: (index % 7 === 0) ? 'none' : '1px solid #e4e4e7',
                          borderBottom: isLastRow ? 'none' : '1px solid #e4e4e7',
                          padding: '8px',
                          backgroundColor: day.isCurrentMonth ? 'white' : '#fafafa',
                          display: 'flex',
                          flexDirection: 'column',
                          boxSizing: 'border-box',
                        }}
                      >
                        <p style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '20px',
                          marginBottom: '8px',
                          color: day.isCurrentMonth ? '#09090b' : '#a1a1aa',
                        }}>
                          {day.dayNumber}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                          {dayEvents.map((event, eventIndex) => {
                            const caregiverInfo = getCaregiverWithAvatar(event.caregiverId);
                            const color = getBookingColor(event, eventIndex);
                            return (
                              <MonthEventCard
                                key={event.id}
                                event={event}
                                caregiverName={caregiverInfo.name}
                                caregiverAvatar={caregiverInfo.avatar}
                                onClick={() => handleBookingClick(event)}
                                color={color}
                                isMobile={isMobile}
                              />
                            );
                          })}
                        </div>

                      </div>
                    );
                  })}
                </div>
              </div>
            ) : viewMode === 'week' ? (
              /* Week View */
              <div style={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    minWidth: isMobile
                      ? `${DAY_COLUMN_WIDTH + displayedCaregivers.length * MOBILE_WEEK_COLUMN_WIDTH}px`
                      : needsHorizontalScroll
                        ? `${DAY_COLUMN_WIDTH + displayedCaregivers.length * WEEK_COLUMN_WIDTH}px`
                        : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                  }}
                >
                  {/* Header with nurse names */}
                  <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e4e4e7',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 10,
                  }}>
                    <div
                      style={{
                        flexShrink: 0,
                        height: '52px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 20,
                        borderRight: '1px solid #e4e4e7',
                        width: DAY_COLUMN_WIDTH,
                        minWidth: DAY_COLUMN_WIDTH,
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#71717a', whiteSpace: 'nowrap' }}>Days</span>
                    </div>
                    {displayedCaregivers.map((caregiver, index) => (
                      <div
                        key={caregiver.id}
                        style={{
                          height: '52px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '0 16px',
                          borderLeft: index === 0 ? 'none' : '1px solid #e4e4e7',
                          width: columnWidth,
                          minWidth: isMobile ? MOBILE_WEEK_COLUMN_WIDTH : WEEK_COLUMN_WIDTH,
                          position: 'relative',
                        }}
                        onMouseEnter={() => setHoveredCaregiverId(caregiver.id)}
                        onMouseLeave={() => setHoveredCaregiverId(null)}
                      >
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: '#dbeafe',
                          flexShrink: 0,
                        }}>
                          <img
                            src={caregiver.avatar}
                            alt={caregiver.fullName}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#0a0a0a',
                            letterSpacing: '-0.14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {caregiver.fullName}
                        </span>
                        {hoveredCaregiverId === caregiver.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#09090b',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              whiteSpace: 'nowrap',
                              zIndex: 1000,
                              marginTop: '4px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            }}
                          >
                            {caregiver.fullName}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Week rows */}
                  {weekDays.map((day, dayIndex) => {
                    const dayName = format(day, 'EEEE');
                    const dateStr = format(day, 'MMM d');

                    return (
                      <div key={dayIndex} style={{ display: 'flex', borderBottom: '1px solid #e4e4e7', flex: 1 }}>
                        <div
                          style={{
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            paddingTop: '12px',
                            paddingLeft: '16px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                            zIndex: 5,
                            borderRight: '1px solid #e4e4e7',
                            borderBottom: '1px solid #e4e4e7',
                            marginBottom: '-1px',
                            width: DAY_COLUMN_WIDTH,
                            minWidth: DAY_COLUMN_WIDTH,
                          }}
                        >
                          <p style={{ fontSize: '14px', fontWeight: 500, color: '#09090b', lineHeight: '14px', whiteSpace: 'nowrap', margin: 0 }}>{dayName}</p>
                          <p style={{ fontSize: '11px', color: '#71717a', lineHeight: '14px', whiteSpace: 'nowrap', margin: '4px 0 0 0' }}>{dateStr}</p>
                        </div>

                        {displayedCaregivers.map((caregiver, cgIndex) => {
                          const dayEvents = getEventsForCaregiverDay(caregiver.id, day);
                          const onLeave = isCaregiverOnLeave(caregiver, day);

                          return (
                            <div
                              key={caregiver.id}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                padding: '8px',
                                borderLeft: cgIndex === 0 ? 'none' : '1px solid #e4e4e7',
                                width: columnWidth,
                                minWidth: isMobile ? MOBILE_WEEK_COLUMN_WIDTH : WEEK_COLUMN_WIDTH,
                              }}
                            >
                              {onLeave && dayEvents.length === 0 ? (
                                <OnLeaveCard />
                              ) : dayEvents.length > 0 ? (
                                dayEvents.map((event, eventIndex) => (
                                  <WeekEventCard
                                    key={event.id}
                                    event={event}
                                    onClick={() => handleBookingClick(event)}
                                    color={getBookingColor(event, eventIndex)}
                                    isMobile={isMobile}
                                  />
                                ))
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Day View */
              <div style={{ height: '100%', overflow: 'auto' }}>
                <div
                  style={{
                    minWidth: isMobile
                      ? `${MOBILE_TIME_COLUMN_WIDTH + displayedCaregivers.length * MIN_COLUMN_WIDTH}px`
                      : needsHorizontalScroll
                        ? `${TIME_COLUMN_WIDTH + displayedCaregivers.length * MIN_COLUMN_WIDTH}px`
                        : '100%'
                  }}
                >
                  {/* Header with nurse info */}
                  <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #e4e4e7',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 10,
                  }}>
                    <div
                      style={{
                        flexShrink: 0,
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 20,
                        borderRight: '1px solid #e4e4e7',
                        width: TIME_COLUMN_WIDTH,
                        minWidth: TIME_COLUMN_WIDTH,
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#71717a', whiteSpace: 'nowrap' }}>GMT +4</span>
                    </div>
                    {displayedCaregivers.map((caregiver, index) => (
                      <div
                        key={caregiver.id}
                        style={{
                          position: 'relative',
                          height: '64px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '0 12px',
                          borderLeft: index === 0 ? 'none' : '1px solid #e4e4e7',
                          width: columnWidth,
                          minWidth: MIN_COLUMN_WIDTH,
                        }}
                        onMouseEnter={() => setHoveredCaregiverId(caregiver.id)}
                        onMouseLeave={() => setHoveredCaregiverId(null)}
                      >
                        <div style={{
                          position: 'relative',
                          width: '32px',
                          height: '32px',
                          flexShrink: 0,
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            backgroundColor: '#dbeafe',
                          }}>
                            <img
                              src={caregiver.avatar}
                              alt={caregiver.fullName}
                              style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                            />
                          </div>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#09090b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            margin: 0,
                            lineHeight: '16px',
                          }}>{caregiver.fullName}</p>
                        </div>
                        {hoveredCaregiverId === caregiver.id && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#09090b',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            marginTop: '4px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                          }}>
                            {caregiver.fullName}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Time grid */}
                  <div style={{ position: 'relative' }}>
                    {timeSlots.map((time) => (
                      <div key={time} style={{
                        display: 'flex',
                        height: '80px',
                        borderBottom: '1px solid #f4f4f5',
                      }}>
                        <div
                          style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            paddingRight: '12px',
                            paddingTop: '10px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                            zIndex: 5,
                            width: TIME_COLUMN_WIDTH,
                            minWidth: TIME_COLUMN_WIDTH,
                            borderBottom: '1px solid #f4f4f5',
                            borderRight: '1px solid #e4e4e7',
                            marginBottom: '-1px',
                          }}
                        >
                          <span style={{ fontSize: '12px', color: '#71717a', whiteSpace: 'nowrap' }}>{time}</span>
                        </div>
                        {displayedCaregivers.map((caregiver, idx) => (
                          <div
                            key={caregiver.id}
                            style={{
                              borderLeft: idx === 0 ? 'none' : '1px solid #f4f4f5',
                              position: 'relative',
                              width: columnWidth,
                              minWidth: MIN_COLUMN_WIDTH,
                            }}
                          />
                        ))}
                      </div>
                    ))}

                    {/* Event cards */}
                    {filteredEvents.map((event, eventIndex) => {
                      const caregiverIndex = displayedCaregivers.findIndex(cg => cg.id === event.caregiverId);
                      if (caregiverIndex === -1) return null;

                      const startHour = event.shift === 'DAY' ? 8 : event.shift === 'NIGHT' ? 20 : 0;
                      const duration = event.hours || 12;

                      const leftOffset = TIME_COLUMN_WIDTH + caregiverIndex * columnWidth + 8;
                      const eventWidth = columnWidth - 16;
                      const isLeave = event.type === 'leave';

                      return (
                        <div
                          key={event.id}
                          onClick={() => handleBookingClick(event)}
                          style={{
                            position: 'absolute',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            color: isLeave ? '#71717a' : 'white',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            transition: 'opacity 0.15s ease',
                            backgroundColor: getBookingColor(event, eventIndex),
                            top: `${startHour * 80 + 8}px`,
                            height: `${Math.min(duration, 24 - startHour) * 80 - 16}px`,
                            left: `${leftOffset}px`,
                            width: `${eventWidth}px`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            border: isLeave ? '2px dashed #9ca3af' : 'none',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                        >
                          {/* Time range */}
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            opacity: 0.85,
                            letterSpacing: '-0.2px',
                          }}>
                            {event.shift === 'DAY' ? '8:00 AM – 8:00 PM' :
                             event.shift === 'NIGHT' ? '8:00 PM – 8:00 AM' :
                             '12:00 AM – 12:00 AM'}
                          </span>

                          {/* Patient name - hero */}
                          <p style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            letterSpacing: '-0.3px',
                            margin: '8px 0 0 0',
                          }}>{event.title}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Drawer for Filters and Nurses (only on mobile) */}
      {isMobile && mobileDrawerOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: `rgba(0, 0, 0, ${mobileDrawerVisible ? 0.3 : 0})`,
              zIndex: 40,
              transition: 'background-color 0.3s ease-out',
            }}
            onClick={closeMobileDrawer}
          />

          {/* Drawer */}
          <div
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              height: '100%',
              width: '85%',
              maxWidth: '360px',
              backgroundColor: 'white',
              boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              transform: mobileDrawerVisible ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.3s ease-out',
            }}
          >
            {/* Close button */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 16px',
              borderBottom: '1px solid #e4e4e7',
            }}>
              <h2 style={{ fontSize: '14px', fontWeight: 600, color: '#09090b', margin: 0 }}>
                Filters
              </h2>
              <button
                onClick={closeMobileDrawer}
                style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: '18px', height: '18px', color: '#71717a' }} />
              </button>
            </div>

            {/* Drawer content - same as desktop left panel */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              {/* Mini Calendar */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px', padding: '13px' }}>
                {/* View Tabs */}
                <div style={{ display: 'flex', alignItems: 'center', borderRadius: '8px', padding: '4px', marginBottom: '17px', backgroundColor: '#f4f4f5' }}>
                  {(['day', 'week', 'month'] as const).map((view) => (
                    <button
                      key={view}
                      onClick={() => handleViewChange(view)}
                      style={{
                        flex: 1,
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        transition: 'all 0.15s',
                        textTransform: 'capitalize',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: viewMode === view ? '#ffffff' : 'transparent',
                        color: viewMode === view ? '#09090b' : '#71717a',
                        boxShadow: viewMode === view ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' : 'none',
                      }}
                    >
                      {view.charAt(0).toUpperCase() + view.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Month Navigation */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '17px', paddingTop: '4px' }}>
                  <button
                    onClick={handlePreviousMonth}
                    style={{
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #f4f4f5',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ChevronLeft style={{ width: '17px', height: '17px', color: '#a1a1aa' }} />
                  </button>
                  <span style={{ fontSize: '15px', fontWeight: 500, color: '#09090b' }}>
                    {format(currentDate, 'MMMM yyyy')}
                  </span>
                  <button
                    onClick={handleNextMonth}
                    style={{
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #f4f4f5',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ChevronRight style={{ width: '17px', height: '17px', color: '#65656e' }} />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {/* Day headers */}
                  <div style={{ display: 'flex' }}>
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                      <div key={day} style={{ width: '44px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '15px', color: '#71717a' }}>{day}</span>
                      </div>
                    ))}
                  </div>
                  {/* Calendar days */}
                  {calendarRows.map((rowIndex) => {
                    const rowDays = calendarDays.filter(d => d.row === rowIndex);
                    const isWeekSelectedRow = viewMode === 'week' && rowDays.some(d => d.isInSelectedWeek);
                    const hasCurrentMonthDays = viewMode === 'month' && rowDays.some(d => d.isCurrentMonth);

                    const firstCurrentMonthIndex = rowDays.findIndex(d => d.isCurrentMonth);
                    const lastCurrentMonthIndex = rowDays.map(d => d.isCurrentMonth).lastIndexOf(true);

                    return (
                      <div
                        key={rowIndex}
                        style={{
                          display: 'flex',
                          position: 'relative',
                          height: '44px',
                        }}
                      >
                        {isWeekSelectedRow && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: `${7 * 44}px`,
                              height: '44px',
                              backgroundColor: '#fce7eb',
                              borderRadius: '9999px',
                            }}
                          />
                        )}
                        {hasCurrentMonthDays && !isWeekSelectedRow && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: `${firstCurrentMonthIndex * 44}px`,
                              width: `${(lastCurrentMonthIndex - firstCurrentMonthIndex + 1) * 44}px`,
                              height: '44px',
                              backgroundColor: '#fce7eb',
                              borderRadius: '9999px',
                            }}
                          />
                        )}
                        {rowDays.map((date, i) => {
                          const isFirstDayOfSelectedWeek = viewMode === 'week' && date.isInSelectedWeek && date.col === 0;
                          const isLastDayOfSelectedWeek = viewMode === 'week' && date.isInSelectedWeek && date.col === 6;
                          const isSelected = viewMode === 'day' && date.isSelected;
                          const isNotCurrentMonth = !date.isCurrentMonth;
                          const isInSelectedWeek = viewMode === 'week' && date.isInSelectedWeek;
                          const isInCurrentMonth = viewMode === 'month' && date.isCurrentMonth;

                          const isFirstDayOfMonth = viewMode === 'month' && date.isCurrentMonth && date.day === 1;
                          const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                          const isLastDayOfMonth = viewMode === 'month' && date.isCurrentMonth && date.day === lastDayOfMonth;

                          let bgColor = 'transparent';
                          let textColor = '#09090b';
                          let opacity = 1;

                          if (isSelected || isLastDayOfMonth) {
                            bgColor = '#ec003f';
                            textColor = '#ffffff';
                          } else if (isFirstDayOfMonth) {
                            bgColor = '#f9b4c4';
                            textColor = '#09090b';
                          } else if (isLastDayOfSelectedWeek) {
                            // In week view, show dark text on pill background (no circle)
                            textColor = '#09090b';
                          } else if (isFirstDayOfSelectedWeek) {
                            // In week view, show dark text on pill background (no circle)
                            textColor = '#09090b';
                          } else if (isNotCurrentMonth && !isInSelectedWeek) {
                            opacity = 0.5;
                            textColor = '#71717a';
                          } else if (isNotCurrentMonth && isInSelectedWeek) {
                            textColor = '#71717a';
                          }

                          const dateKey = `${rowIndex}-${i}`;
                          const canHover = !isSelected && !isFirstDayOfSelectedWeek && !isLastDayOfSelectedWeek && !isInSelectedWeek && !isInCurrentMonth;
                          const isHovered = hoveredDateKey === dateKey && canHover;
                          const finalBgColor = isHovered ? '#f4f4f5' : bgColor;

                          return (
                            <div
                              key={i}
                              onClick={() => handleDateSelect(date.date)}
                              style={{
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                fontWeight: 500,
                                letterSpacing: '-0.06em',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s',
                                backgroundColor: finalBgColor,
                                color: textColor,
                                borderRadius: '50%',
                                opacity: opacity,
                                position: 'relative',
                                zIndex: 1,
                              }}
                              onMouseEnter={() => {
                                if (canHover) {
                                  setHoveredDateKey(dateKey);
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredDateKey(null);
                              }}
                            >
                              {date.day}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Nurses Section */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users style={{ width: '14px', height: '14px', color: '#71717a' }} />
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#09090b' }}>
                      Nurses ({selectedCaregiverIds.length} / {allCaregivers.length})
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    onClick={handleSelectAllNurses}
                  >
                    <span style={{ fontSize: '11px', color: '#71717a' }}>Select all</span>
                    <Checkbox checked={areAllNursesSelected} onClick={handleSelectAllNurses} />
                  </div>
                </div>

                <div style={{ padding: '0 12px 8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 7px', backgroundColor: '#f4f4f5', borderRadius: '6px' }}>
                    <Search style={{ width: '13px', height: '13px', color: '#71717a' }} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={nurseSearchQuery}
                      onChange={(e) => setNurseSearchQuery(e.target.value)}
                      style={{
                        flex: 1,
                        backgroundColor: 'transparent',
                        fontSize: '14px',
                        color: '#09090b',
                        border: 'none',
                        outline: 'none',
                      }}
                    />
                  </div>
                </div>

                <div style={{ padding: '0 12px 12px 12px', maxHeight: '300px', overflowY: 'auto' }}>
                  {filteredCaregivers.map((caregiver) => {
                    const isSelected = selectedCaregiverIds.includes(caregiver.id);
                    return (
                      <div
                        key={caregiver.id}
                        onClick={() => handleNurseToggle(caregiver.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '11px 8px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Checkbox checked={isSelected} onClick={() => handleNurseToggle(caregiver.id)} />
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden', backgroundColor: '#dbeafe' }}>
                          <img
                            src={caregiver.avatar}
                            alt={caregiver.fullName}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                          />
                        </div>
                        <span style={{ fontSize: '14px', color: '#0a0a0a', letterSpacing: '-0.15px' }}>
                          {caregiver.fullName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Availability Section */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e4e4e7', borderRadius: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5.83312 11.6667C5.83307 11.7751 5.86323 11.8813 5.92021 11.9736C5.97719 12.0658 6.05874 12.1403 6.15571 12.1888L7.32237 12.7721C7.41133 12.8165 7.51017 12.8375 7.60951 12.833C7.70886 12.8285 7.8054 12.7987 7.88997 12.7464C7.97455 12.6941 8.04434 12.621 8.09273 12.5341C8.14113 12.4473 8.1665 12.3494 8.16646 12.25V8.16667C8.16659 7.87756 8.27406 7.59879 8.46804 7.38442L12.6815 2.72417C12.757 2.64049 12.8066 2.53673 12.8244 2.42543C12.8422 2.31413 12.8274 2.20006 12.7817 2.09701C12.7361 1.99396 12.6615 1.90635 12.5671 1.84476C12.4727 1.78318 12.3625 1.75027 12.2498 1.75H1.74979C1.63698 1.75004 1.5266 1.78279 1.43202 1.84429C1.33744 1.90578 1.26272 1.99338 1.21692 2.09648C1.17111 2.19957 1.15618 2.31374 1.17394 2.42514C1.19169 2.53655 1.24137 2.64042 1.31696 2.72417L5.53154 7.38442C5.72552 7.59879 5.83299 7.87756 5.83312 8.16667V11.6667Z" stroke="#71717a" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#09090b' }}>Availability</span>
                </div>

                <div style={{ padding: '0 12px 8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 7px', backgroundColor: '#f4f4f5', borderRadius: '6px' }}>
                    <Search style={{ width: '13px', height: '13px', color: '#71717a' }} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={availabilitySearchQuery}
                      onChange={(e) => setAvailabilitySearchQuery(e.target.value)}
                      style={{
                        flex: 1,
                        backgroundColor: 'transparent',
                        fontSize: '14px',
                        color: '#09090b',
                        border: 'none',
                        outline: 'none',
                      }}
                    />
                  </div>
                </div>

                <div style={{ padding: '0 12px 12px 12px' }}>
                  {filteredAvailabilityOptions.map((filter) => (
                    <div
                      key={filter.id}
                      onClick={() => handleAvailabilityToggle(filter.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <Checkbox checked={filters.statusFilter.includes(filter.id)} onClick={() => handleAvailabilityToggle(filter.id)} />
                      <span style={{ fontSize: '14px', color: '#0a0a0a', letterSpacing: '-0.15px' }}>{filter.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Booking Detail Drawer */}
      <BookingDrawer
        booking={selectedBooking}
        caregiver={selectedBookingCaregiver}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />

      {/* Available Caregivers Modal */}
      <AvailableCaregiversModal
        isOpen={availabilityModalOpen}
        onClose={() => {
          setAvailabilityModalOpen(false);
          setSelectedDayAvailability(null);
        }}
        date={selectedDayAvailability?.date || new Date()}
        caregivers={selectedDayAvailability?.caregivers || []}
      />

      {/* Universal Search Dialog (Desktop only) */}
      {!isMobile && (
        <CalendarSearchDialog
          open={searchOpen}
          onOpenChange={setSearchOpen}
          caregivers={allCaregivers}
          events={searchEvents}
          currentDate={currentDate}
          onCaregiverSelect={(caregiver) => {
            setSelectedCaregivers([caregiver.id]);
            setCurrentDate(new Date());
            setSearchOpen(false);
          }}
          onAssignmentSelect={(event) => {
            setCurrentDate(event.startDate);
            setSelectedCaregivers([event.caregiverId]);
            setSelectedBooking(event);
            setIsDrawerOpen(true);
            setSearchOpen(false);
          }}
          onDateSelect={(date) => {
            setCurrentDate(date);
            setSearchOpen(false);
          }}
        />
      )}
    </div>
  );
}
